import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    getSubjectMonitoringConfig,
    // eslint-disable-next-line max-len
    getSubjectMonitoringConfig_OpraQueries_dataListingsConfigTableForStudy_DataListingsConfig_dataListingsItems_DataListingsListItem as ConfigDataListingItem,
    getSubjectMonitoringConfigVariables,
    updateSubjectMonitoringConfig,
    updateSubjectMonitoringConfigVariables,
    dataListingsTablesForStudy,
    dataListingsTablesForStudyVariables,
} from '@queries/generated/subject-monitoring.generated';
import {
    GET_DATA_LISTINGS_TABLES_FOR_STUDY,
    GET_SUBJECT_MONITORING_CONFIGURATION,
    UPDATE_SUBJECT_MONITORING_CONFIGURATION,
} from '@queries/subject-monitoring';

const COMPONENT_NAME = 'subjectMonitoringConfig';

interface ISubjectMonitoringPanels {
    display: (DragDropListItemProps & IPanel)[];
    available: (DragDropListItemProps & IPanel)[];
}

interface ISubjectMonitoringConfigProps {
    studyRef: string;
    canEdit: boolean;
}

interface IPanel {
    name: string;
    active: boolean;
    datatableId: string;
    recordId: number;
}

const SubjectMonitoringConfig = ({ studyRef, canEdit }: ISubjectMonitoringConfigProps) => {
    const { confirmDiscardChanges, componentHasChanges, setComponentHasChanges } = useUnsavedChangesContext();
    const { t } = useTranslation('admin');
    const isEditMode = useBoolean(false);
    const [currentState, setCurrentState] = useState<ISubjectMonitoringPanels>({
        display: [],
        available: [],
    });
    const [panelsToSave, setPanelsToSave] = useState<IPanel[]>([]);
    const [initState, setInitState] = useState<(DragDropListItemProps & IPanel)[]>([]);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [prevKey, setPrevKey] = useState<number>(0);

    const [
        loadSubjectConfig,
        { data: subjectConfigData, loading: subjectConfigLoading, error: subjectConfigDataError, refetch: fetchConfig },
    ] = useLazyQuery<getSubjectMonitoringConfig, getSubjectMonitoringConfigVariables>(
        GET_SUBJECT_MONITORING_CONFIGURATION,
    );

    const [
        loadDataListingsForStudy,
        { data: dataListingsData, loading: dataListingsLoading, error: dataListingsDataError },
    ] = useLazyQuery<dataListingsTablesForStudy, dataListingsTablesForStudyVariables>(
        GET_DATA_LISTINGS_TABLES_FOR_STUDY,
    );

    const [saveConfig, { loading: isMutatingConfigData, error: mutationError }] = useMutation<
        updateSubjectMonitoringConfig,
        updateSubjectMonitoringConfigVariables
    >(UPDATE_SUBJECT_MONITORING_CONFIGURATION, {
        refetchQueries: [
            {
                query: GET_DATA_LISTINGS_TABLES_FOR_STUDY,
                variables: { studyId: studyRef },
            },
        ],
    });

    const isDataListingActive = (item: ConfigDataListingItem | null): boolean => {
        if (!dataListingsData?.dataListingsForStudy || !item) {
            return false;
        }

        const matchedItem = dataListingsData.dataListingsForStudy.find((x) => x?.id === item?.datatableId);

        return matchedItem?.active ?? false;
    };

    useEffect(() => {
        loadSubjectConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        loadDataListingsForStudy({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const transformAndSortData = () => {
        const subjectConfigDataDisplay: (DragDropListItemProps & IPanel)[] = (
            subjectConfigData?.dataListingsConfigTableForStudy?.dataListingsItems ?? []
        ).map((item) => {
            const liveItem = dataListingsData?.dataListingsForStudy?.find((x) => x?.id === item?.datatableId);
            return {
                datatableId: item?.datatableId ?? '',
                title: liveItem?.name ?? item?.name ?? '',
                name: liveItem?.name ?? item?.name ?? '',
                active: isDataListingActive(item),
                recordId: item?.recordId as number,
            };
        });
        const newDisplayData: (DragDropListItemProps & IPanel)[] =
            dataListingsData?.dataListingsForStudy?.map((item) => ({
                datatableId: item?.id ?? '',
                title: item?.name ?? '',
                name: item?.name ?? '',
                active: item?.active ?? false,
                recordId: item?.recordId,
            })) ?? [];

        let filteredData: (DragDropListItemProps & IPanel)[] = newDisplayData;

        if (subjectConfigDataDisplay.length && newDisplayData.length) {
            filteredData = newDisplayData.filter(
                (item) => !subjectConfigDataDisplay.some((displayItem) => displayItem.datatableId === item.datatableId),
            );
        }

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setInitState(subjectConfigDataDisplay);
        setPanelsToSave(subjectConfigDataDisplay);

        setCurrentState(() => ({
            display: subjectConfigDataDisplay,
            available: filteredData,
        }));
        /* eslint-enable @eslint-react/set-state-in-effect */
    };

    useEffect(() => {
        transformAndSortData();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [subjectConfigData, dataListingsData]);

    const onEdit = () => {
        isEditMode.setTrue();
    };

    useEffect(() => {
        const isChanges =
            JSON.stringify(panelsToSave.map((w) => w.recordId)) !== JSON.stringify(initState.map((w) => w.recordId));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setHasChanges(isChanges);
    }, [panelsToSave, initState]);

    const onCancel = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const ok = await confirmDiscardChanges('confirmation-dialog');
            if (!ok) return;
        }

        transformAndSortData();
        isEditMode.setFalse();
        setComponentHasChanges(COMPONENT_NAME, false);
    };

    const onSave = async () => {
        try {
            await saveConfig({
                variables: {
                    studyId: studyRef,
                    fields: panelsToSave.map((item) => ({
                        datatableId: item?.datatableId as string,
                        name: item?.name as string,
                        recordId: item?.recordId,
                    })),
                },
            });

            isEditMode.setFalse();
            setComponentHasChanges(COMPONENT_NAME, false);
            ShowSuccess(t('common:actionsSuccessfulToast'));
            await fetchConfig();
            setPrevKey((prevState) => prevState + 1);
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    const isLoading =
        !subjectConfigData || subjectConfigLoading || !dataListingsData || dataListingsLoading || isMutatingConfigData;
    const isError = subjectConfigDataError || dataListingsDataError || mutationError;

    return (
        <EditCard
            key={prevKey}
            title={t('subjectConfig.profile')}
            onEdit={onEdit}
            onCancel={onCancel}
            onSave={onSave}
            isEditMode={isEditMode.value}
            disabled={!canEdit || isLoading}
            data-testid="subject-monitoring-config"
            saveDisabled={!hasChanges && !componentHasChanges(COMPONENT_NAME)}
        >
            {isLoading && !isError && <Loading />}
            {isError && <ErrorMessage />}
            {!isLoading && !isError && (
                <div style={{ height: '230px' }}>
                    <DragDropListsCore
                        list1Header={t('subjectConfig.dataListingsToDisplay')}
                        list2Header={t('subjectConfig.availableDataListings')}
                        list1Items={currentState.display}
                        list2Items={currentState.available}
                        isTwoLists
                        idField="datatableId"
                        onChangeItemsLeft={(display) => {
                            const saveDisplay = display.map((item) => item as unknown as IPanel);
                            setPanelsToSave(saveDisplay);
                        }}
                        disabled={!isEditMode.value}
                        className="k-mb-4"
                    />
                </div>
            )}
        </EditCard>
    );
};

export default SubjectMonitoringConfig;
